import type { ComponentChildren } from "preact"
import type { QuestionImage } from "../types"
import { IconX } from "./Icons"
import { QuestionPicture } from "./QuestionPicture"

interface BoardShellProps {
	// Status line content (question counter, scores) shown above the prompt.
	header: ComponentChildren
	prompt: string
	image: QuestionImage | null
	// The answer area: the second half of .board-split. It is the only part
	// of the play surface that differs between the two kinds of question.
	children: ComponentChildren
	// Optional content under the answer area (waiting notices, errors).
	footer?: ComponentChildren
}

// BoardShell is everything a play surface has that is not the answering: the
// status line with its way out, and the prompt+image half. Both boards are
// this shell wrapped around their own answer area — QuestionBoard's four
// choice buttons, or SpellBoard's tiles — so the layout, the leave link and
// the portrait/landscape split are written once (see .board-* in styles.css).
//
// Whatever goes in `children` is the second child of .board-split, which is
// what the landscape media query sizes as the other column. It must fit
// without scrolling: it is where the controls are, and the .board-* rules
// exist to keep them all on screen.
export function BoardShell({
	header,
	prompt,
	image,
	children,
	footer,
}: BoardShellProps) {
	return (
		// No py-* here: the vertical padding is in .board so the landscape
		// media query can shrink it.
		<div class="board container h-100 stack">
			{/* The app navbar is hidden while playing, so the board carries its own
			    way out. */}
			<div class="flex between center gap-3 small">
				<a href="/" class="plain lh-1 muted" aria-label="Leave">
					<IconX size="1.25rem" />
				</a>
				{header}
			</div>

			{/* No .stack here: the landscape media query flips the direction, and
			    it must win over the utility. */}
			<div class="board-split grow flex overflow-hidden">
				{/* The picture shrinks to fit (see .board-image), so at every real
				    screen size this never overflows and no scrollbar appears.
				    overflow-auto is the safety net for when it somehow does: clipping
				    would hide part of the question with no way to reach it, which is
				    strictly worse than a scrollbar. This is the prompt half only — the
				    answer area below must never scroll, because it is where the
				    controls are. */}
				<div class="board-prompt stack jc-center center text-center gap-3 py-2 overflow-auto">
					{/* A text-only prompt is the whole visual, so it gets to be bigger;
					    with an image the image carries the weight. */}
					<h1 class={image ? undefined : "big"}>{prompt}</h1>
					{image && <QuestionPicture image={image} variant="board" />}
				</div>

				{children}
			</div>

			{footer}
		</div>
	)
}
